import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import Swal from 'sweetalert2'
import {KTIcon, toAbsoluteUrl} from '../../../_metronic/helpers'
import {useAuth} from '../../../app/modules/auth'
import {getUserByToken} from '../../../app/modules/auth/core/_requests'
import {InviteUsers} from '../../../_metronic/partials/modals/invite-users/InviteUsers'
import {PurchaseSignalModal} from '../../../_metronic/partials/modals/purchase-signal/PurchaseSignalModal'
import {UpgradePlan} from '../../../_metronic/partials/modals/upgrade-plan/UpgradePlan'
import {AddCreditModal} from '../../../_metronic/partials/modals/add-credit/AddCreditModal'
import {fetchSignals, purchaseSignal, fetchPurchasedSignals, addCredit, consumeAnalysis, checkCryptoPaymentStatus, getPendingCryptoInvoice} from './_requests'
import {TradingViewLightChart} from './components/TradingViewLightChart'

const parsePriceValue = (value: unknown): number | undefined => {
  if (value === null || value === undefined) return undefined
  const numericValue = typeof value === 'number' ? value : parseFloat(value as string)
  return Number.isFinite(numericValue) ? numericValue : undefined
}

const parsePositivePriceValue = (value: unknown): number | undefined => {
  const numericValue = parsePriceValue(value)
  return typeof numericValue === 'number' && numericValue > 0 ? numericValue : undefined
}

const shouldShowSignalPriceLines = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === '1' || normalized === 'true' || normalized === 'yes'
  }
  return false
}

const buildTakeProfitLevels = (signal: any): number[] => {
  const candidates = [
    signal.take_profit_1,
    signal.take_profit_2,
    signal.take_profit_3,
    signal.take_profit_4,
  ]
  return candidates
    .map(parsePositivePriceValue)
    .filter((value): value is number => typeof value === 'number')
}

const buildHighlightBoxes = (signal: any) => {
  if (!signal.areas || !Array.isArray(signal.areas)) {
    // Fallback for old signals that still have the single area fields
    const low = parsePriceValue(signal.box_low)
    const high = parsePriceValue(signal.box_high)
    if (typeof low === 'number' && typeof high === 'number') {
      const color =
        typeof signal.box_color === 'string' && signal.box_color ? signal.box_color : '#22c55e'
      const opacityRaw = signal.box_opacity
      const opacity =
        typeof opacityRaw === 'number'
          ? opacityRaw
          : opacityRaw
          ? parseFloat(opacityRaw)
          : undefined
      const borderColor =
        typeof signal.box_border_color === 'string' && signal.box_border_color
          ? signal.box_border_color
          : color
      return [
        {
          low,
          high,
          fromTime: signal.box_from_time,
          toTime: signal.box_to_time,
          color,
          opacity: typeof opacity === 'number' && !Number.isNaN(opacity) ? opacity : 0.18,
          borderColor,
          label:
            typeof signal.box_label === 'string' && signal.box_label ? signal.box_label : undefined,
        },
      ]
    }
    return []
  }

  return signal.areas
    .map((area: any) => {
      const low = parsePriceValue(area.box_low)
      const high = parsePriceValue(area.box_high)
      if (typeof low !== 'number' || typeof high !== 'number') return null

      const color =
        typeof area.box_color === 'string' && area.box_color ? area.box_color : '#22c55e'
      const opacityRaw = area.box_opacity
      const opacity =
        typeof opacityRaw === 'number'
          ? opacityRaw
          : opacityRaw
          ? parseFloat(opacityRaw)
          : undefined
      const borderColor =
        typeof area.box_border_color === 'string' && area.box_border_color
          ? area.box_border_color
          : color

      return {
        low,
        high,
        fromTime: area.box_from_time,
        toTime: area.box_to_time,
        color,
        opacity: typeof opacity === 'number' && !Number.isNaN(opacity) ? opacity : 0.18,
        borderColor,
        label: typeof area.box_label === 'string' && area.box_label ? area.box_label : undefined,
      }
    })
    .filter((box: any) => box !== null)
}

const extractSignalId = (value: any): number | null => {
  const rawId = value?.signal_id ?? value?.id
  const parsedId = Number(rawId)
  return Number.isFinite(parsedId) ? parsedId : null
}

const hasPurchasedSignal = (purchasedIds: number[], signal: any): boolean => {
  const signalId = extractSignalId(signal)
  return signalId !== null && purchasedIds.includes(signalId)
}

const normalizePurchasedSignalIds = (payload: any): number[] => {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.purchased_signals)
    ? payload.purchased_signals
    : []

  return Array.from(
    new Set(source.map((item: any) => extractSignalId(item)).filter((id: number | null): id is number => id !== null))
  )
}

/** Locale for date display: fa = Persian (Shamsi), en = Gregorian, ar = Islamic (Hijri/Qamari) */
const getDateLocale = (locale: string): string => {
  if (locale === 'fa') return 'fa-IR-u-ca-persian' // شمسی
  if (locale === 'ar') return 'ar-SA-u-ca-islamic'  // قمری
  return 'en-US-u-ca-gregory' // میلادی
}

const isLikelyJalaliDate = (value: string): boolean => {
  const jalaliPattern = /^(\d{4})\/(\d{2})\/(\d{2})$/
  const match = value.match(jalaliPattern)
  if (!match) return false
  const year = Number(match[1])
  return year >= 1300 && year <= 1600
}

const toEnglishDigits = (value: string): string =>
  value
    .replace(/[۰-۹]/g, (digit) => String(digit.charCodeAt(0) - 1776))
    .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 1632))

type ForexSession = {
  key: string
  title: string
  startUTC: number
  endUTC: number
  gradient: string
}

const forexSessionsConfig: Omit<ForexSession, 'title'>[] = [
  {key: 'sydney', startUTC: 22, endUTC: 7, gradient: 'linear-gradient(135deg, #22d3ee, #0ea5e9)'},
  {key: 'tokyo', startUTC: 0, endUTC: 9, gradient: 'linear-gradient(135deg, #a855f7, #6366f1)'},
  {key: 'london', startUTC: 7, endUTC: 16, gradient: 'linear-gradient(135deg, #34d399, #22c55e)'},
  {key: 'newyork', startUTC: 12, endUTC: 21, gradient: 'linear-gradient(135deg, #f97316, #ef4444)'},
]

const formatTimeSpan = (ms: number): string => {
  if (ms <= 0) return '00:00:00'
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

const formatRemainingPlanTime = (
  expiry: string | null | undefined,
  intl: ReturnType<typeof useIntl>
): string | null => {
  if (!expiry) return null
  const expiryDate = new Date(expiry)
  if (Number.isNaN(expiryDate.getTime())) return null
  const diff = expiryDate.getTime() - Date.now()
  if (diff <= 0) return null

  const totalMinutes = Math.floor(diff / 60000)
  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
  const minutes = totalMinutes % 60

  const parts: string[] = []
  if (days > 0) {
    parts.push(
      intl.formatMessage({id: 'TIME.DAY_COUNT'}, {count: days})
    )
  }
  if (hours > 0) {
    parts.push(
      intl.formatMessage({id: 'TIME.HOUR_COUNT'}, {count: hours})
    )
  }
  if (days === 0 && minutes > 0) {
    parts.push(
      intl.formatMessage({id: 'TIME.MINUTE_COUNT'}, {count: minutes})
    )
  }

  return parts.join(` ${intl.formatMessage({id: 'COMMON.AND'})} `)
}

const getSessionTiming = (
  session: Omit<ForexSession, 'title'>,
  reference: Date,
  locale: string
) => {
  const start = new Date(
    Date.UTC(
      reference.getUTCFullYear(),
      reference.getUTCMonth(),
      reference.getUTCDate(),
      session.startUTC,
      0,
      0
    )
  )
  const end = new Date(start.getTime())
  if (session.endUTC <= session.startUTC) {
    end.setUTCDate(end.getUTCDate() + 1)
  }
  end.setUTCHours(session.endUTC, 0, 0, 0)

  const localeTag = locale === 'fa' ? 'fa-IR' : locale === 'ar' ? 'ar' : 'en-US'
  const sessionTimeFormatter = new Intl.DateTimeFormat(localeTag, {
    hour: '2-digit',
    minute: '2-digit',
  })
  const rangeStart = sessionTimeFormatter.format(start)
  const rangeEnd = sessionTimeFormatter.format(end)
  const isActive = reference >= start && reference < end
  const inFuture = reference < start
  const duration = end.getTime() - start.getTime()
  const elapsed = Math.min(Math.max(reference.getTime() - start.getTime(), 0), duration)
  const progress = duration > 0 ? (isActive ? elapsed / duration : reference >= end ? 1 : 0) : 0
  const countdownTarget = isActive ? end : start
  const timeRemaining = countdownTarget.getTime() - reference.getTime()
  const timeStr = formatTimeSpan(timeRemaining)

  let statusLabelKey = 'DASHBOARD.SESSION.STATUS_ENDED'
  let timeLabelKey = 'DASHBOARD.SESSION.TIME_ENDED'
  let timeLabelValues: {time?: string} = {}
  if (isActive) {
    statusLabelKey = 'DASHBOARD.SESSION.STATUS_ACTIVE'
    timeLabelKey = 'DASHBOARD.SESSION.TIME_UNTIL_END'
    timeLabelValues = {time: timeStr}
  } else if (inFuture) {
    statusLabelKey = 'DASHBOARD.SESSION.STATUS_PENDING'
    timeLabelKey = 'DASHBOARD.SESSION.TIME_UNTIL_START'
    timeLabelValues = {time: timeStr}
  }

  return {
    rangeStart,
    rangeEnd,
    isActive,
    inFuture,
    progress: Math.min(Math.max(progress, 0), 1),
    statusLabelKey,
    timeLabelKey,
    timeLabelValues,
  }
}

const DashboardWrapper: FC = () => {
  const {currentUser, setCurrentUser} = useAuth()
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false)
  const [refIncomePercent, setRefIncomePercent] = useState<number>(10)
  const [signals, setSignals] = useState<any[]>([])
  const [signalsLoading, setSignalsLoading] = useState<boolean>(false)
  const [signalsError, setSignalsError] = useState<string | null>(null)
  const [purchasedSignalIds, setPurchasedSignalIds] = useState<number[]>([])
  const [showPurchaseModal, setShowPurchaseModal] = useState<boolean>(false)
  const [selectedSignalForPurchase, setSelectedSignalForPurchase] = useState<any>(null)
  const [purchaseLoading, setPurchaseLoading] = useState<boolean>(false)
  const [currentTime, setCurrentTime] = useState<Date>(new Date())
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false)
  const [showAddCreditModal, setShowAddCreditModal] = useState<boolean>(false)
  const [addCreditLoading, setAddCreditLoading] = useState<boolean>(false)
  const [viewedAnalysisIds, setViewedAnalysisIds] = useState<number[]>(
    () => currentUser?.analyses_used ?? []
  )

  const analysisAccessReason = useMemo<'free' | 'expired' | 'limit' | null>(() => {
    const plan = currentUser?.plan
    const expiry = currentUser?.plan_expiry
    const limit = currentUser?.analysis_limit ?? 0
    const used = currentUser?.analyses_used_count ?? 0

    if (!plan || (plan !== 'basic' && plan !== 'pro' && plan !== 'premium')) return 'free'
    if (expiry) {
      const expiryDate = new Date(expiry)
      if (!Number.isNaN(expiryDate.getTime()) && expiryDate <= new Date()) return 'expired'
    }
    if (limit === 0 || (limit !== -1 && used >= limit)) return 'limit'
    return null
  }, [currentUser?.plan, currentUser?.plan_expiry, currentUser?.analysis_limit, currentUser?.analyses_used_count])

  const canAccessAnalyses = useMemo(() => {
    const plan = currentUser?.plan
    const expiry = currentUser?.plan_expiry
    const limit = currentUser?.analysis_limit ?? 0
    const used = currentUser?.analyses_used_count ?? 0
    if (!plan) return false
    if (plan !== 'basic' && plan !== 'pro' && plan !== 'premium') return false
    if (expiry) {
      const expiryDate = new Date(expiry)
      if (Number.isNaN(expiryDate.getTime()) || expiryDate <= new Date()) return false
    }
    if (limit === 0) return false
    if (limit !== -1 && used >= limit) return false
    return true
  }, [currentUser?.plan, currentUser?.plan_expiry, currentUser?.analysis_limit, currentUser?.analyses_used_count])

  useEffect(() => {
    const p = Number(currentUser?.ref_percent)
    if (!Number.isNaN(p) && p >= 0) setRefIncomePercent(p)
  }, [currentUser?.ref_percent])

  // Check for pending crypto payment on mount to show indicator on dashboard
  useEffect(() => {
    getPendingCryptoInvoice()
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (currentUser?.analyses_used) {
      setViewedAnalysisIds(prev => {
        const merged = Array.from(new Set([...prev, ...(currentUser.analyses_used ?? [])]))
        return merged
      })
    }
  }, [currentUser?.analyses_used])

  // Function to fetch signals and purchased signals
  const fetchSignalsData = async (showLoading = true) => {
    if (showLoading) {
      setSignalsLoading(true)
      setSignalsError(null)
    }

    // Fetch signals and purchased signals separately to handle errors independently
    await Promise.allSettled([fetchSignals(), fetchPurchasedSignals()])
      .then(([signalsResult, purchasedResult]) => {
        // Handle signals
        if (signalsResult.status === 'fulfilled') {
          setSignals(signalsResult.value.data)
        } else {
          console.error('Error fetching signals:', signalsResult.reason)
          if (showLoading) {
            setSignalsError(intl.formatMessage({id: 'DASHBOARD.SIGNALS_FETCH_ERROR'}))
          }
          // Don't clear signals on polling errors, only on initial load
          if (showLoading) {
            setSignals([])
          }
        }

        // Handle purchased signals - don't fail if this fails
        if (purchasedResult.status === 'fulfilled') {
          setPurchasedSignalIds(normalizePurchasedSignalIds(purchasedResult.value.data))
        } else {
          console.warn('Error fetching purchased signals:', purchasedResult.reason)
          // Don't set error, just use empty array
          if (showLoading) {
            setPurchasedSignalIds([])
          }
        }
      })
      .finally(() => {
        if (showLoading) {
          setSignalsLoading(false)
        }
      })
  }

  // Initial fetch on mount
  useEffect(() => {
    fetchSignalsData(true)
  }, [])

  // Polling: fetch signals every minute (60 seconds) for live area updates
  useEffect(() => {
    const pollingInterval = setInterval(() => {
      // Fetch signals silently (without showing loading state)
      fetchSignalsData(false)
    }, 60000) // 60 seconds = 1 minute

    return () => {
      clearInterval(pollingInterval)
    }
  }, [])

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const intl = useIntl()
  const dateLocale = getDateLocale(intl.locale)
  const simpleDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
    []
  )
  const formatHeroDate = useCallback(
    (date: Date): string => {
      const weekday = new Intl.DateTimeFormat(dateLocale, {weekday: 'long'}).format(date)
      const day = new Intl.DateTimeFormat(dateLocale, {day: 'numeric'}).format(date)
      const month = new Intl.DateTimeFormat(dateLocale, {month: 'long'}).format(date)
      const year = new Intl.DateTimeFormat(dateLocale, {year: 'numeric'}).format(date)
      return `${weekday} ${day} ${month} ${year}`
    },
    [dateLocale]
  )
  const formatSimpleDate = useCallback(
    (value?: string | null): string => {
      if (!value) return '-'
      const normalizedValue = typeof value === 'string' ? toEnglishDigits(value.trim()) : String(value)
      if (isLikelyJalaliDate(normalizedValue)) return normalizedValue.replace(/\//g, '-')
      if (/^\d{4}[/-]\d{2}[/-]\d{2}$/.test(normalizedValue)) {
        return normalizedValue.replace(/\//g, '-')
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return normalizedValue
      return toEnglishDigits(simpleDateFormatter.format(date))
    },
    [simpleDateFormatter]
  )
  const heroDate = formatHeroDate(currentTime)
  const userWallet = currentUser?.wallet || 0
  const remainingPlanTime = useMemo(
    () => formatRemainingPlanTime(currentUser?.plan_expiry, intl),
    [currentTime, currentUser?.plan_expiry, intl]
  )
  const remainingAnalysisCount = useMemo(() => {
    const limit = currentUser?.analysis_limit
    const used = currentUser?.analyses_used_count ?? 0
    if (!currentUser?.plan || limit === undefined || limit === 0) return null
    if (limit === -1) return -1
    return Math.max(limit - used, 0)
  }, [currentUser?.plan, currentUser?.analysis_limit, currentUser?.analyses_used_count])

  const handleSignalClick = async (signal: any) => {
    if (signal.type === 'free' || signal.price === 0 || !signal.price) return
    const signalId = extractSignalId(signal)
    if (signalId === null) return
    try {
      const res = await fetchPurchasedSignals()
      const normalizedLatestIds = normalizePurchasedSignalIds(res.data)
      setPurchasedSignalIds(normalizedLatestIds)
      if (normalizedLatestIds.includes(signalId)) return
    } catch {
      if (purchasedSignalIds.includes(signalId)) return
    }
    setSelectedSignalForPurchase(signal)
    setShowPurchaseModal(true)
  }


  const handlePurchaseConfirm = async () => {
    if (!selectedSignalForPurchase) return

    setPurchaseLoading(true)
    try {
      const response = await purchaseSignal(selectedSignalForPurchase.id)
      if (response.data.status === true) {
        // به‌روزرسانی wallet کاربر
        if (currentUser && response.data.wallet !== undefined) {
          setCurrentUser({...currentUser, wallet: response.data.wallet})
        }
        // اضافه کردن به لیست سیگنال‌های خریداری شده
        setPurchasedSignalIds((prev) => {
          const purchasedId = extractSignalId(selectedSignalForPurchase)
          if (purchasedId === null || prev.includes(purchasedId)) return prev
          return [...prev, purchasedId]
        })
        // بستن مودال
        setShowPurchaseModal(false)
        setSelectedSignalForPurchase(null)
      } else {
        alert(response.data.message || intl.formatMessage({id: 'DASHBOARD.PURCHASE_ERROR'}))
      }
    } catch (error: any) {
      alert(error.response?.data?.message || intl.formatMessage({id: 'DASHBOARD.PURCHASE_ERROR'}))
    } finally {
      setPurchaseLoading(false)
    }
  }

  const handleUpgradeClose = useCallback(() => {
    setShowUpgradeModal(false)
    getUserByToken()
      .then(({data: user}) => {
        if (user) setCurrentUser(user)
      })
      .catch(() => {})
  }, [setCurrentUser])

  const handleRequestAnalysisAccess = useCallback(() => {
    let text = intl.formatMessage({id: 'DASHBOARD.ANALYSIS_ACCESS_MODAL_FREE'})
    if (analysisAccessReason === 'expired') {
      text = intl.formatMessage({id: 'DASHBOARD.ANALYSIS_ACCESS_MODAL_EXPIRED'})
    } else if (analysisAccessReason === 'limit') {
      text = intl.formatMessage({id: 'DASHBOARD.ANALYSIS_ACCESS_MODAL_LIMIT'})
    }

    Swal.fire({
      icon: 'warning',
      title: intl.formatMessage({id: 'DASHBOARD.ANALYSIS_ACCESS_MODAL_TITLE'}),
      text,
      showCancelButton: true,
      confirmButtonText: intl.formatMessage({id: 'DASHBOARD.ANALYSIS_UPGRADE_BUTTON'}),
      cancelButtonText: intl.formatMessage({id: 'UPGRADE.CANCEL'}),
    }).then((result) => {
      if (result.isConfirmed) {
        setShowUpgradeModal(true)
      }
    })
  }, [analysisAccessReason, intl])

  const handleAddCreditConfirm = useCallback(
    async (_amount: number) => {
      // Credit is now added via crypto payment; this callback is kept for interface compatibility
    },
    []
  )

  // Handle payment success redirect from NOWPayments
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const paymentResult = params.get('payment')
    const oid = params.get('order_id')

    if (paymentResult === 'success' && oid) {
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname)

      const checkPayment = async () => {
        try {
          const res = await checkCryptoPaymentStatus(oid)
          if (res.data.wallet !== undefined && currentUser) {
            setCurrentUser({...currentUser, wallet: res.data.wallet})
          }
          if (res.data.payment_status === 'finished' || res.data.payment_status === 'confirmed') {
            Swal.fire({
              icon: 'success',
              title: intl.formatMessage({id: 'CRYPTO.SUCCESS_TITLE'}),
              text: intl.formatMessage({id: 'CRYPTO.SUCCESS_DESC'}, {credits: res.data.credit_amount || 0}),
              confirmButtonText: intl.formatMessage({id: 'UPGRADE.CONFIRM_BUTTON'}),
            })
          }
        } catch {
          // silently fail
        }
      }
      checkPayment()
    } else if (paymentResult === 'cancelled') {
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  return (
    <>
      <section className='hero-section dashboard-hero contact-hero mt-2'>
        <div className='hero-content'>
          <div className='mb-4'>
            <span className='badge px-4 py-2 rounded-pill'>
              <KTIcon iconName='element-11' className='fs-2 text-warning ms-2' />
              {intl.formatMessage({id: 'DASHBOARD.HERO.BADGE'})}
            </span>
          </div>
          <h1 className='hero-title'>
            {intl.formatMessage({id: 'DASHBOARD.HERO.TITLE'}, {date: heroDate})}
          </h1>
          <div className='row g-4 mt-5'>
            <div className='col-12 col-md-6'>
              <div
                className='card border-0 overflow-hidden h-100'
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.12) 0%, rgba(255, 152, 0, 0.08) 100%)',
                  boxShadow: '0 4px 20px rgba(255, 152, 0, 0.15)',
                  borderRadius: '16px',
                }}
              >
                <div className='card-body p-4 p-md-5 d-flex flex-column justify-content-between'>
                  <div className='d-flex align-items-start justify-content-between mb-3'>
                    <div
                      className='rounded-3 d-flex align-items-center justify-content-center'
                      style={{
                        width: 48,
                        height: 48,
                        background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                        boxShadow: '0 4px 12px rgba(255, 152, 0, 0.35)',
                      }}
                    >
                      <KTIcon iconName='wallet' className='fs-2 text-white' />
                    </div>
                    <span className='badge badge-light-warning fs-8 fw-bold px-3 py-2'>
                      {intl.formatMessage({id: 'UPGRADE.UNIT_CREDIT'})}
                    </span>
                  </div>
                  <div className='mb-1 text-gray-600 fs-7 fw-semibold'>
                    {intl.formatMessage({id: 'DASHBOARD.KPI.CREDIT'})}
                  </div>
                  <div className='fs-1 fw-bolder text-gray-900 mb-4' style={{letterSpacing: '-0.02em'}}>
                    {userWallet.toLocaleString(intl.locale === 'fa' ? 'fa-IR' : 'en-US')}
                  </div>
                  <button
                    type='button'
                    onClick={() => setShowAddCreditModal(true)}
                    className='btn btn-warning fw-bold w-100 py-3 rounded-3 position-relative'
                    style={{boxShadow: '0 4px 14px rgba(255, 152, 0, 0.4)'}}
                  >
                    {intl.formatMessage({id: 'DASHBOARD.KPI.INCREASE_CREDIT_BUTTON'})}
                  </button>
                </div>
              </div>
            </div>
            <div className='col-12 col-md-6'>
              <div
                className='card border-0 overflow-hidden h-100'
                style={{
                  background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.1) 0%, rgba(102, 16, 242, 0.08) 100%)',
                  boxShadow: '0 4px 20px rgba(13, 110, 253, 0.15)',
                  borderRadius: '16px',
                }}
              >
                <div className='card-body p-4 p-md-5 d-flex flex-column justify-content-between'>
                  <div className='d-flex align-items-start justify-content-between mb-3'>
                    <div
                      className='rounded-3 d-flex align-items-center justify-content-center'
                      style={{
                        width: 48,
                        height: 48,
                        background: 'linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)',
                        boxShadow: '0 4px 12px rgba(13, 110, 253, 0.4)',
                      }}
                    >
                      <KTIcon iconName='chart-line' className='fs-2 text-white' />
                    </div>
                    {(currentUser?.plan === 'basic' ||
                      currentUser?.plan === 'pro' ||
                      currentUser?.plan === 'premium') && (
                      <span className='badge badge-light-success fs-8 fw-bold px-3 py-2'>
                        {intl.formatMessage({id: 'DASHBOARD.STATUS.ACTIVE'})}
                      </span>
                    )}
                  </div>
                  <div className='mb-1 text-gray-600 fs-7 fw-semibold'>
                    {intl.formatMessage({id: 'DASHBOARD.KPI.CURRENT_PLAN'})}
                  </div>
                  <div className='fs-1 fw-bolder text-gray-900 mb-4' style={{letterSpacing: '-0.02em'}}>
                    {!currentUser?.plan
                      ? intl.formatMessage({id: 'UPGRADE.PLAN_FREE'})
                      : currentUser?.plan === 'pro'
                      ? intl.formatMessage({id: 'UPGRADE.PLAN_PRO'})
                      : currentUser?.plan === 'premium'
                      ? intl.formatMessage({id: 'UPGRADE.PLAN_PREMIUM'})
                      : intl.formatMessage({id: 'UPGRADE.PLAN_BASIC'})}
                  </div>
                  {currentUser?.plan && remainingPlanTime && (
                    <div className='mb-2 text-gray-600 fs-7 fw-semibold'>
                      {intl.formatMessage(
                        {id: 'DASHBOARD.KPI.PLAN_REMAINING'},
                        {time: remainingPlanTime}
                      )}
                    </div>
                  )}
                  {currentUser?.plan && remainingAnalysisCount !== null && (
                    <div className='mb-4 text-gray-600 fs-7 fw-semibold'>
                      {remainingAnalysisCount === -1
                        ? intl.formatMessage({id: 'DASHBOARD.KPI.ANALYSES_REMAINING_UNLIMITED'})
                        : intl.formatMessage(
                            {id: 'DASHBOARD.KPI.ANALYSES_REMAINING'},
                            {count: remainingAnalysisCount}
                          )}
                    </div>
                  )}
                  <button
                    type='button'
                    onClick={() => setShowUpgradeModal(true)}
                    className='btn btn-primary fw-bold w-100 py-3 rounded-3'
                    style={{boxShadow: '0 4px 14px rgba(13, 110, 253, 0.4)'}}
                  >
                    {intl.formatMessage({id: 'DASHBOARD.KPI.UPGRADE_BUTTON'})}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='hero-image d-flex align-items-center justify-content-center'>
          <div
            className='forex-session-box text-white w-100'
            style={{
              maxWidth: '420px',
            }}
          >
            <div className='d-flex flex-column gap-3'>
              {forexSessionsConfig.map((session) => {
                const {rangeStart, rangeEnd, isActive, progress, statusLabelKey, timeLabelKey, timeLabelValues} =
                  getSessionTiming(session, currentTime, intl.locale)
                const progressDeg = `${progress * 360}deg`
                const sessionTitleKey = `DASHBOARD.SESSION.${session.key.toUpperCase()}` as 'DASHBOARD.SESSION.SYDNEY' | 'DASHBOARD.SESSION.TOKYO' | 'DASHBOARD.SESSION.LONDON' | 'DASHBOARD.SESSION.NEWYORK'
                const range = intl.formatMessage({id: 'DASHBOARD.SESSION.RANGE'}, {start: rangeStart, end: rangeEnd})
                const statusLabel = intl.formatMessage({id: statusLabelKey})
                const timeLabel = intl.formatMessage({id: timeLabelKey}, timeLabelValues)
                const circleText = isActive || timeLabelValues.time ? timeLabelValues.time : intl.formatMessage({id: 'DASHBOARD.SESSION.TIME_ENDED'})
                return (
                  <div
                    key={session.key}
                    className='p-4 rounded-3'
                    style={{
                      background: session.gradient,
                      border: isActive
                        ? '2px solid rgba(255,255,255,0.85)'
                        : '1px solid rgba(255,255,255,0.2)',
                      boxShadow: isActive ? '0 15px 40px rgba(0,0,0,0.25)' : 'none',
                    }}
                  >
                    <div className='d-flex align-items-center gap-4'>
                      <div className='position-relative' style={{width: 92, height: 92}}>
                        <div
                          className='w-100 h-100 rounded-circle'
                          style={{
                            background: `conic-gradient(rgba(255,255,255,0.95) ${progressDeg}, rgba(255,255,255,0.25) 0deg)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.6s ease',
                          }}
                        >
                          <div
                            className='rounded-circle bg-white text-dark text-center d-flex flex-column align-items-center justify-content-center'
                            style={{width: 70, height: 70}}
                          >
                            <span className='fw-bold fs-6' style={{lineHeight: 1.2}}>
                              {circleText}
                            </span>
                            {isActive && <span className='text-muted fs-8'>{intl.formatMessage({id: 'DASHBOARD.SESSION.REMAINING'})}</span>}
                          </div>
                        </div>
                      </div>
                      <div className='flex-grow-1'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                          <span className='fw-semibold'>{intl.formatMessage({id: sessionTitleKey})}</span>
                          <span className='badge badge-light fw-bold'>{statusLabel}</span>
                        </div>
                        <div className='fs-5 fw-bold'>{range}</div>
                        <div className='text-white-80 fs-8 mt-2'>{timeLabel}</div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SIGNAL CENTER */}
      <section className='service-highlights-section py-10'>
        <div className='text-center mb-6'>
          <h2 className='mb-3 section-title'>{intl.formatMessage({id: 'DASHBOARD.SIGNAL_CENTER.TITLE'})}</h2>
          <p className='text-gray-600 fs-5 section-desc'>
            {intl.formatMessage({id: 'DASHBOARD.SIGNAL_CENTER.DESC'})}
          </p>
        </div>
        <SignalTabs
          signals={signals}
          loading={signalsLoading}
          error={signalsError}
          purchasedSignalIds={purchasedSignalIds}
          onSignalClick={handleSignalClick}
          intl={intl}
          formatSimpleDate={formatSimpleDate}
          canAccessAnalyses={canAccessAnalyses}
          analysisAccessReason={analysisAccessReason}
          viewedAnalysisIds={viewedAnalysisIds}
          onRequestUpgrade={() => setShowUpgradeModal(true)}
          onRequestAnalysisAccess={handleRequestAnalysisAccess}
          onBeforeViewAnalysis={async (signalId: number) => {
            if (viewedAnalysisIds.includes(signalId)) return true
            try {
              await consumeAnalysis(signalId)
              setViewedAnalysisIds(prev => [...prev, signalId])
              const {data: user} = await getUserByToken()
              setCurrentUser(user)
              return true
            } catch (e: any) {
              if (e?.response?.status === 403) {
                const msg = e?.response?.data?.message || intl.formatMessage({id: 'DASHBOARD.ANALYSIS_LIMIT_REACHED'})
                await Swal.fire({ icon: 'warning', title: '', text: msg })
              }
              return false
            }
          }}
        />
      </section>

      {/* REFERRAL CTA */}
      <section className='final-cta-section py-10'>
        <div className='container'>
          <div className='cta-card'>
            <div className='row align-items-center gy-4'>
              <div className='col-lg-7'>
                <div className='cta-content'>
                  <h3 className='cta-title text-dark mb-4'>{intl.formatMessage({id: 'DASHBOARD.REFERRAL.TITLE'})}</h3>
                  <p className='cta-description fs-5 mb-6'>
                    {intl.formatMessage({id: 'DASHBOARD.REFERRAL.DESC'}, {percent: refIncomePercent})}
                  </p>
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className='custom-btn btn-custom-font btn-custom-color rounded-pill btn-lg px-7'
                    aria-label={intl.formatMessage({id: 'DASHBOARD.REFERRAL.ARIA'})}
                    type='button'
                  >
                    {intl.formatMessage({id: 'DASHBOARD.REFERRAL.BUTTON'})}
                  </button>
                </div>
              </div>
              <div className='col-lg-5 text-center'>
                <img
                  src={toAbsoluteUrl('/media/illustrations/energy/winner.png')}
                  alt={intl.formatMessage({id: 'DASHBOARD.REFERRAL.IMAGE_ALT'})}
                  className='img-fluid'
                  style={{maxHeight: 260}}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Invite modal */}
      {showInviteModal && (
        <InviteUsers
          onClose={() => setShowInviteModal(false)}
          refIncomePercent={refIncomePercent}
        />
      )}

      {/* Purchase Signal Modal */}
      {showPurchaseModal && selectedSignalForPurchase && (
        <PurchaseSignalModal
          onClose={() => {
            setShowPurchaseModal(false)
            setSelectedSignalForPurchase(null)
          }}
          onConfirm={handlePurchaseConfirm}
          signalTitle={selectedSignalForPurchase.title}
          signalPrice={selectedSignalForPurchase.price || 0}
          userWallet={userWallet}
          loading={purchaseLoading}
        />
      )}

      {/* Add Credit Modal (Crypto Payment) */}
      {showAddCreditModal && (
        <AddCreditModal
          onClose={() => {
            setShowAddCreditModal(false)
            getUserByToken()
              .then(({data: user}) => {
                if (user) setCurrentUser(user)
              })
              .catch(() => {})
          }}
          onConfirm={handleAddCreditConfirm}
          currentWallet={userWallet}
          loading={addCreditLoading}
        />
      )}

      {/* Upgrade Plan Modal (for analysis access) */}
      {showUpgradeModal && (
        <>
          <div
            className='modal-backdrop fade show'
            style={{zIndex: 1040}}
            onClick={handleUpgradeClose}
            onKeyDown={(e) => e.key === 'Escape' && handleUpgradeClose()}
            role='button'
            tabIndex={0}
            aria-label={intl.formatMessage({id: 'DASHBOARD.MODAL_CLOSE'})}
          />
          <div style={{zIndex: 1045, position: 'fixed', inset: 0, overflow: 'auto'}}>
            <UpgradePlan
              onClose={handleUpgradeClose}
              onRequestAddCredit={() => setShowAddCreditModal(true)}
            />
          </div>
        </>
      )}
    </>
  )
}

export {DashboardWrapper}

// --- SignalTabs component (demo data, Persian UI) ---
type SignalType = 'all' | 'analysis' | 'signals' | 'forex' | 'crypto' | 'index' | 'stock' | 'commodity' | 'etf' | 'purchased'

const signalTabKeys: {key: SignalType; labelKey: string}[] = [
  {key: 'all', labelKey: 'DASHBOARD.SIGNAL_TAB.ALL'},
  {key: 'analysis', labelKey: 'DASHBOARD.SIGNAL_TAB.ANALYSIS'},
  {key: 'signals', labelKey: 'DASHBOARD.SIGNAL_TAB.SIGNALS'},
  {key: 'forex', labelKey: 'DASHBOARD.SIGNAL_TAB.FOREX'},
  {key: 'crypto', labelKey: 'DASHBOARD.SIGNAL_TAB.CRYPTO'},
  {key: 'index', labelKey: 'DASHBOARD.SIGNAL_TAB.INDEX'},
  {key: 'stock', labelKey: 'DASHBOARD.SIGNAL_TAB.STOCK'},
  {key: 'commodity', labelKey: 'DASHBOARD.SIGNAL_TAB.COMMODITY'},
  {key: 'etf', labelKey: 'DASHBOARD.SIGNAL_TAB.ETF'},
  {key: 'purchased', labelKey: 'DASHBOARD.SIGNAL_TAB.PURCHASED'},
]

interface SignalTabsProps {
  signals: any[]
  loading: boolean
  error: string | null
  purchasedSignalIds: number[]
  onSignalClick: (signal: any) => void
  intl: ReturnType<typeof useIntl>
  formatSimpleDate: (value?: string | null) => string
  canAccessAnalyses: boolean
  analysisAccessReason: 'free' | 'expired' | 'limit' | null
  viewedAnalysisIds: number[]
  onRequestUpgrade: () => void
  onRequestAnalysisAccess: () => void
  onBeforeViewAnalysis?: (signalId: number) => Promise<boolean>
}
const SignalTabs: FC<SignalTabsProps> = ({
  signals,
  loading,
  error,
  purchasedSignalIds,
  onSignalClick,
  intl,
  formatSimpleDate,
  canAccessAnalyses,
  analysisAccessReason,
  viewedAnalysisIds,
  onRequestUpgrade,
  onRequestAnalysisAccess,
  onBeforeViewAnalysis,
}) => {
  const [activeTab, setActiveTab] = useState<SignalType>('all')
  const [selectedSignal, setSelectedSignal] = useState<number | null>(null)
  const [chartIntervalBySignal, setChartIntervalBySignal] = useState<Record<number, string>>({})

  const hasStopLoss = (s: any): boolean => {
    const sl = s.stop_loss
    if (sl === null || sl === undefined) return false
    if (typeof sl === 'number') return sl !== 0 && !Number.isNaN(sl)
    const parsed = parseFloat(sl)
    return !Number.isNaN(parsed) && parsed !== 0
  }

  const filteredSignals =
    activeTab === 'all'
      ? signals
      : activeTab === 'purchased'
      ? signals.filter((s) => {
          const signalId = extractSignalId(s)
          return (
            (signalId !== null && purchasedSignalIds.includes(signalId)) ||
            (signalId !== null && viewedAnalysisIds.includes(signalId))
          )
        })
      : activeTab === 'signals'
      ? signals.filter((s) => hasStopLoss(s))
      : activeTab === 'analysis'
      ? signals.filter((s) => !hasStopLoss(s))
      : signals.filter((s) => s.type === activeTab)

  const handleOpenTradingView = (e: React.MouseEvent, url: string) => {
    e.preventDefault()
    // باز کردن پنجره popup مینیمال در مرورگر
    const width = 1400
    const height = 900
    const left = window.screen.width / 2 - width / 2
    const top = window.screen.height / 2 - height / 2

    window.open(
      url,
      'TradingView',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`
    )
  }
  if (loading) {
    return (
      <div className='text-center py-10'>
        <span className='spinner-border text-primary' role='status'></span>
        <div className='mt-3 text-gray-600'>{intl.formatMessage({id: 'DASHBOARD.SIGNAL_LOADING'})}</div>
      </div>
    )
  }
  if (error) {
    return <div className='text-center text-danger py-6'>{error}</div>
  }

  return (
    <div id='signals' className='card card-flush mb-10'>
      {/* Professional Header */}
      <div className='card-header pb-0 bg-white border-bottom'>
        {/* Professional Tabs */}
        <div className='mt-4'>
          <ul className='nav nav-tabs nav-tabs-line'>
            {signalTabKeys.map((tab) => (
              <li key={tab.key} className='nav-item'>
                <button
                  type='button'
                  className={`nav-link fw-semibold px-4 py-3 w-100 ${
                    activeTab === tab.key ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab(tab.key as SignalType)}
                  style={{minHeight: '48px', touchAction: 'manipulation', fontSize: '1rem'}}
                >
                  {intl.formatMessage({id: tab.labelKey})}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className='card-body p-0'>
        <div className='row g-0'>
          {/* Signals List - Minimal & Clean */}
          <div className='col-12 col-lg-5 col-xl-3'>
            <div className='p-4 p-lg-5 border-end border-lg-end'>
              <div
                className='d-flex flex-column gap-3'
                style={{maxHeight: '700px', overflowY: 'auto'}}
              >
                {filteredSignals.length > 0 ? (
                  filteredSignals.map((signal) => {
                    const signalId = extractSignalId(signal)
                    const isPurchased = hasPurchasedSignal(purchasedSignalIds, signal)
                    const isFree = signal.type === 'free' || signal.price === 0 || !signal.price
                    const isAnalysisItem = !hasStopLoss(signal)
                    const canView = isAnalysisItem
                      ? (signalId !== null && (canAccessAnalyses || viewedAnalysisIds.includes(signalId)))
                      : isPurchased || isFree
                    const contentTypeLabel = isAnalysisItem
                      ? intl.formatMessage({id: 'DASHBOARD.CONTENT_TYPE.ANALYSIS'})
                      : intl.formatMessage({id: 'DASHBOARD.CONTENT_TYPE.SIGNAL'})
                    const contentTypeBadgeClass = isAnalysisItem
                      ? 'badge-light-primary'
                      : 'badge-light-warning'
                    const numericPrice = parsePriceValue(signal.price) ?? 0
                    const creditUnit = intl.formatMessage({id: 'DASHBOARD.CREDIT_UNIT'})
                    const freeLabel = intl.formatMessage({id: 'DASHBOARD.FREE'})
                    const noExpiry = intl.formatMessage({id: 'DASHBOARD.NO_EXPIRY'})
                    const formattedPrice =
                      numericPrice > 0
                        ? `${numericPrice.toLocaleString(intl.locale === 'fa' ? 'fa-IR' : 'en-US')} ${creditUnit}`
                        : freeLabel
                    const createdDateLabel = formatSimpleDate(signal.date || signal.created_at)
                    const expiryLabel = signal.expiry_date
                      ? formatSimpleDate(signal.expiry_date)
                      : noExpiry
                    const marketLabel =
                      signal.type === 'crypto'
                        ? intl.formatMessage({id: 'DASHBOARD.SIGNAL_TAB.CRYPTO'})
                        : signal.type === 'forex'
                        ? intl.formatMessage({id: 'DASHBOARD.SIGNAL_TAB.FOREX'})
                        : signal.type === 'index'
                        ? intl.formatMessage({id: 'DASHBOARD.SIGNAL_TAB.INDEX'})
                        : signal.type === 'stock'
                        ? intl.formatMessage({id: 'DASHBOARD.SIGNAL_TAB.STOCK'})
                        : signal.type === 'commodity'
                        ? intl.formatMessage({id: 'DASHBOARD.SIGNAL_TAB.COMMODITY'})
                        : signal.type === 'etf'
                        ? intl.formatMessage({id: 'DASHBOARD.SIGNAL_TAB.ETF'})
                        : intl.formatMessage({id: 'DASHBOARD.MARKET.OTHER'})
                    const marketBadgeClass =
                      signal.type === 'crypto'
                        ? 'badge-light-info'
                        : signal.type === 'forex'
                        ? 'badge-light-primary'
                        : signal.type === 'index'
                        ? 'badge-light-warning'
                        : signal.type === 'stock'
                        ? 'badge-light-success'
                        : signal.type === 'commodity'
                        ? 'badge-light-danger'
                        : signal.type === 'etf'
                        ? 'badge-light-dark'
                        : 'badge-light-warning'
                    const assetLabel = signal.asset || signal.symbol || signal.tv_symbol || ''

                    return (
                      <div
                        key={signal.id}
                        className={`card border border-2 ${
                          selectedSignal === signal.id
                            ? 'border-primary shadow-lg'
                            : isAnalysisItem
                            ? 'border-primary shadow'
                            : 'border-warning shadow'
                        }`}
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          borderRadius: '8px',
                          opacity: canView ? 1 : 0.7,
                        }}
                        onClick={() => {
                          if (
                            signalId !== null &&
                            isAnalysisItem &&
                            (canAccessAnalyses || viewedAnalysisIds.includes(signalId)) &&
                            onBeforeViewAnalysis
                          ) {
                            onBeforeViewAnalysis(signalId).then((ok) => {
                              if (ok) setSelectedSignal(signalId)
                            })
                          } else if (
                            signalId !== null &&
                            isAnalysisItem &&
                            !canAccessAnalyses &&
                            !viewedAnalysisIds.includes(signalId)
                          ) {
                            onRequestAnalysisAccess()
                          } else if (canView) {
                            if (signalId !== null) {
                              setSelectedSignal(signalId)
                            }
                          } else {
                            onSignalClick(signal)
                          }
                        }}
                      >
                        <div className='card-body p-4 d-flex flex-column gap-3'>
                          <div className='d-flex justify-content-between align-items-start'>
                            <div className='flex-grow-1'>
                              <h6 className='fw-bold text-gray-900 mb-2 fs-5'>{signal.title}</h6>
                              <div className='d-flex flex-wrap gap-2'>
                                <span className={`badge fs-6 px-3 py-2 ${contentTypeBadgeClass}`}>
                                  {contentTypeLabel}
                                </span>
                                {assetLabel && (
                                  <span className={`badge fs-6 px-3 py-2 ${marketBadgeClass}`}>
                                    {assetLabel}
                                  </span>
                                )}
                                <span className={`badge fs-6 px-3 py-2 ${marketBadgeClass}`}>
                                  {marketLabel}
                                </span>
                                <span
                                  className={`badge fs-6 px-3 py-2 ${
                                    signal.status === 'active'
                                      ? 'badge-light-success'
                                      : 'badge-light-secondary'
                                  }`}
                                >
                                  {signal.status === 'active'
                                    ? intl.formatMessage({id: 'DASHBOARD.STATUS.ACTIVE'})
                                    : intl.formatMessage({id: 'DASHBOARD.STATUS.CLOSED'})}
                                </span>
                                {isPurchased && (
                                  <span className='badge fs-6 px-3 py-2 badge-light-success'>
                                    {intl.formatMessage({id: 'DASHBOARD.SIGNAL_TAB.PURCHASED'})}
                                  </span>
                                )}
                                {signalId !== null && isAnalysisItem && viewedAnalysisIds.includes(signalId) && (
                                  <span className='badge fs-6 px-3 py-2 badge-light-info'>
                                    {intl.formatMessage({id: 'DASHBOARD.ANALYSIS_VIEWED'})}
                                  </span>
                                )}
                              </div>
                            </div>
                            {!isPurchased && !isAnalysisItem && (
                              <div className='text-end'>
                                <div className='text-gray-600 fs-7'>{intl.formatMessage({id: 'DASHBOARD.SIGNAL_PRICE_LABEL'})}</div>
                                <div className='fw-bold text-gray-900 fs-4'>{formattedPrice}</div>
                              </div>
                            )}
                          </div>
                          <div className='d-flex flex-wrap gap-3 text-gray-700 fs-6'>
                            <div className='d-flex flex-column'>
                              <span className='text-muted fs-8'>{intl.formatMessage({id: 'DASHBOARD.CREATED_DATE'})}</span>
                              <span className='fw-semibold'>{createdDateLabel}</span>
                            </div>
                            <div className='d-flex flex-column'>
                              <span className='text-muted fs-8'>{intl.formatMessage({id: 'DASHBOARD.EXPIRY_LABEL'})}</span>
                              <span className='fw-semibold'>{expiryLabel}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className='text-center py-5'>
                    <KTIcon iconName='chart-simple' className='fs-2x text-muted mb-2' />
                    <p className='text-muted small mb-0'>{intl.formatMessage({id: 'DASHBOARD.NO_SIGNALS'})}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Signal Details - Minimal & Clean */}
          <div className='col-12 col-lg-7 col-xl-9'>
            <div className='p-4 p-lg-5'>
              {selectedSignal ? (
                (() => {
                  const signal = signals.find((s) => extractSignalId(s) === selectedSignal)
                  if (!signal) return null
                  const signalId = extractSignalId(signal)
                  const isPurchased = hasPurchasedSignal(purchasedSignalIds, signal)
                  const isFree = signal.type === 'free' || signal.price === 0 || !signal.price
                  const isAnalysis = !hasStopLoss(signal)
                  const canView = isAnalysis
                    ? (signalId !== null && (canAccessAnalyses || viewedAnalysisIds.includes(signalId)))
                    : isPurchased || isFree
                  const detailContentTypeLabel = isAnalysis
                    ? intl.formatMessage({id: 'DASHBOARD.CONTENT_TYPE.ANALYSIS'})
                    : intl.formatMessage({id: 'DASHBOARD.CONTENT_TYPE.SIGNAL'})
                  const detailContentTypeBadgeClass = isAnalysis
                    ? 'badge-light-primary'
                    : 'badge-light-warning'
                  const detailCreatedDate = formatSimpleDate(signal.date || signal.created_at)
                  const detailExpiryLabel = signal.expiry_date
                    ? formatSimpleDate(signal.expiry_date)
                    : intl.formatMessage({id: 'DASHBOARD.NO_EXPIRY'})
                  const normalizeIntervalByType = (
                    iv?: string,
                    type?: 'crypto' | 'forex' | 'index' | 'stock' | 'commodity' | 'etf'
                  ): string => {
                    const v = (iv || '').toString().trim().toLowerCase()
                    const isForex = type === 'forex'
                    if (isForex) {
                      if (v === '1') return '1'
                      if (v === '5') return '5'
                      if (v === '15') return '15'
                      if (v === '30') return '30'
                      if (v === '60' || v === '1h') return '60'
                      if (v === '240' || v === '4h') return '240'
                      if (v === '1d' || v === 'd' || v === 'daily') return '1d'
                      if (v === '1w' || v === 'w' || v === 'weekly') return '1w'
                      if (v === '1m' || v === 'm' || v === 'monthly' || v === '1M') return '1M'
                      return '1d'
                    }
                    // crypto
                    if (v === '15') return '15'
                    if (v === '30') return '30'
                    if (v === '60' || v === '1h') return '60'
                    if (v === '240' || v === '4h') return '240'
                    if (v === '1d' || v === 'd' || v === 'daily') return '1d'
                    if (v === '3d') return '3d'
                    if (v === '1w' || v === 'w' || v === 'weekly') return '1w'
                    if (v === '1m' || v === 'm' || v === 'monthly' || v === '1M') return '1M'
                    return '60'
                  }
                  const signalIntervalRaw =
                    signal.interval ?? signal.timeframe ?? signal.chart_interval ?? signal.default_interval
                  const backendInterval = normalizeIntervalByType(
                    signalIntervalRaw,
                    (['crypto', 'forex', 'index', 'stock', 'commodity', 'etf'].includes(signal.type) ? signal.type : undefined) as 'crypto' | 'forex' | 'index' | 'stock' | 'commodity' | 'etf' | undefined
                  )
                  const currentInterval =
                    (signalId !== null ? chartIntervalBySignal[signalId] : undefined) || backendInterval
                  const intervalOptions =
                    signal.type === 'forex'
                      ? ['1', '5', '15', '30', '60', '240', '1d', '1w', '1M']
                      : ['15', '30', '60', '240', '1d', '3d', '1w', '1M']
                  const getIntervalLabel = (v: string): string =>
                    intl.formatMessage({id: `DASHBOARD.INTERVAL.${v}`}, {defaultMessage: v})
                  return (
                    <div className='d-flex flex-column h-100'>
                      {!canView && !isAnalysis && (
                        <div className='alert alert-warning mb-4'>
                          <div className='d-flex align-items-center'>
                            <KTIcon iconName='information-5' className='fs-2x text-warning me-4' />
                            <div className='flex-grow-1'>
                              <div className='fw-bold mb-2 fs-5'>{intl.formatMessage({id: 'DASHBOARD.SIGNAL_PURCHASABLE_TITLE'})}</div>
                              <div className='text-gray-700 fs-6'>
                                {intl.formatMessage({id: 'DASHBOARD.SIGNAL_PURCHASABLE_DESC'})}
                              </div>
                            </div>
                            <button
                              type='button'
                              onClick={() => onSignalClick(signal)}
                              className='btn btn-warning fw-semibold'
                              style={{
                                minHeight: '44px',
                                touchAction: 'manipulation',
                                fontSize: '1rem',
                              }}
                            >
                              {intl.formatMessage({id: 'DASHBOARD.BUY_SIGNAL_BUTTON'})}
                            </button>
                          </div>
                        </div>
                      )}
                      {!canView && isAnalysis && (() => {
                        const isLimitReached = analysisAccessReason === 'limit'
                        const titleKey = isLimitReached ? 'DASHBOARD.ANALYSIS_LIMIT_TITLE' : 'DASHBOARD.ANALYSIS_UPGRADE_TITLE'
                        const descKey = isLimitReached ? 'DASHBOARD.ANALYSIS_LIMIT_DESC' : 'DASHBOARD.ANALYSIS_UPGRADE_DESC'
                        return (
                          <div className='alert alert-warning mb-4'>
                            <div className='d-flex align-items-center'>
                              <KTIcon iconName='chart-line' className='fs-2x text-warning me-4' />
                              <div className='flex-grow-1'>
                                <div className='fw-bold mb-2 fs-5'>{intl.formatMessage({id: titleKey})}</div>
                                <div className='text-gray-700 fs-6'>
                                  {intl.formatMessage({id: descKey})}
                                </div>
                              </div>
                              <button
                                type='button'
                                onClick={onRequestAnalysisAccess}
                                className='btn btn-warning fw-semibold'
                                style={{
                                  minHeight: '44px',
                                  touchAction: 'manipulation',
                                  fontSize: '1rem',
                                }}
                              >
                                {intl.formatMessage({id: 'DASHBOARD.ANALYSIS_UPGRADE_BUTTON'})}
                              </button>
                            </div>
                          </div>
                        )
                      })()}
                      {/* Trading Details - Compact Grid */}
                      {canView && (
                        <div className='card mb-5'>
                          <div className='card-header d-flex justify-content-between align-items-center'>
                            <div className='card-title m-0'>
                              <span
                                className={`me-2 badge fs-5 px-3 py-2 ${detailContentTypeBadgeClass}`}
                              >
                                {detailContentTypeLabel}
                              </span>
                              <span
                                className={`me-2 badge fs-5 px-3 py-2 ${
                                  signal.type === 'crypto'
                                    ? 'badge-light-info'
                                    : signal.type === 'forex'
                                    ? 'badge-light-primary'
                                    : signal.type === 'index'
                                    ? 'badge-light-warning'
                                    : signal.type === 'stock'
                                    ? 'badge-light-success'
                                    : signal.type === 'commodity'
                                    ? 'badge-light-danger'
                                    : signal.type === 'etf'
                                    ? 'badge-light-dark'
                                    : 'badge-light-success'
                                }`}
                              >
                                {signal.asset}
                              </span>
                              <span
                                className={`me-2 badge fs-5 px-3 py-2 ${
                                  signal.type === 'crypto'
                                    ? 'badge-light-primary'
                                    : signal.type === 'forex'
                                    ? 'badge-light-info'
                                    : signal.type === 'index'
                                    ? 'badge-light-warning'
                                    : signal.type === 'stock'
                                    ? 'badge-light-success'
                                    : signal.type === 'commodity'
                                    ? 'badge-light-danger'
                                    : 'badge-light-dark'
                                }`}
                              >
                                {signal.type === 'crypto'
                                  ? 'BINANCE'
                                  : signal.type === 'forex'
                                  ? 'OANDA'
                                  : signal.type === 'index'
                                  ? intl.formatMessage({id: 'DASHBOARD.SIGNAL_TAB.INDEX'})
                                  : signal.type === 'stock'
                                  ? intl.formatMessage({id: 'DASHBOARD.SIGNAL_TAB.STOCK'})
                                  : signal.type === 'commodity'
                                  ? intl.formatMessage({id: 'DASHBOARD.SIGNAL_TAB.COMMODITY'})
                                  : signal.type === 'etf'
                                  ? intl.formatMessage({id: 'DASHBOARD.SIGNAL_TAB.ETF'})
                                  : signal.type}
                              </span>
                              {signal.stop_loss != 0 && signal.take_profit_1 != 0 && signal.direction && (
                                <span
                                  className={`me-2 badge fs-5 px-3 py-2 ${
                                    signal.direction === 'buy' ? 'badge-success' : 'badge-danger'
                                  }`}
                                >
                                  {signal.direction === 'buy'
                                    ? intl.formatMessage({id: 'DASHBOARD.DIRECTION.BUY'})
                                    : intl.formatMessage({id: 'DASHBOARD.DIRECTION.SELL'})}
                                </span>
                              )}
                              <span
                                className={`me-2 badge fs-5 px-3 py-2 ${
                                  signal.status === 'active'
                                    ? 'badge-light-success'
                                    : 'badge-light-secondary'
                                }`}
                              >
                                {signal.status === 'active'
                                  ? intl.formatMessage({id: 'DASHBOARD.STATUS.ACTIVE'})
                                  : intl.formatMessage({id: 'DASHBOARD.STATUS.CLOSED'})}
                              </span>
                            </div>
                            <div className='d-flex align-items-center gap-2'>
                              <span className='text-muted fs-7'>{intl.formatMessage({id: 'DASHBOARD.TIMEFRAME_LABEL'})}</span>
                              <select
                                className='form-select form-select-sm w-auto'
                                value={currentInterval}
                                onChange={(e) => 
                                  signalId !== null &&
                                  setChartIntervalBySignal({
                                    ...chartIntervalBySignal,
                                    [signalId]: e.target.value,
                                  })
                                }
                              >
                                {intervalOptions.map((iv) => (
                                  <option key={iv} value={iv}>
                                    {getIntervalLabel(iv)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className='card-body'>
                            <div className='mb-4'>
                              <TradingViewLightChart
                                key={signalId ?? signal.id}
                                symbol={
                                  signal.tv_symbol ||
                                  signal.symbol ||
                                  signal.asset ||
                                  'BINANCE:BTCUSDT'
                                }
                                interval={currentInterval}
                                height={520}
                                entryPrice={parsePositivePriceValue(signal.entry_price)}
                                stopLoss={parsePositivePriceValue(signal.stop_loss)}
                                takeProfits={buildTakeProfitLevels(signal)}
                                market={
                                  ['crypto', 'forex', 'index', 'stock', 'commodity', 'etf'].includes(signal.type)
                                    ? signal.type
                                    : undefined
                                }
                                highlightBoxes={buildHighlightBoxes(signal)}
                                showPriceLines={shouldShowSignalPriceLines(signal.show_tp_sl_lines)}
                              />
                            </div>
                            {signal.chartUrl != 0 && (
                              <button
                                type='button'
                                onClick={(e) => handleOpenTradingView(e, signal.chartUrl)}
                                className='btn btn-primary w-100 fw-semibold'
                                style={{
                                  minHeight: '48px',
                                  touchAction: 'manipulation',
                                  fontSize: '1rem',
                                }}
                              >
                                <KTIcon iconName='external-link' className='fs-6 me-2' />
                                {intl.formatMessage({id: 'CHART.OPEN_IN_TRADINGVIEW'})}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      <div className='row g-4 mb-4 text-center'>
                        {signal.entry_price != 0 && (
                          <div className='col-4 col-md-2 col-lg-2'>
                            <div className='card bg-light-primary border-0'>
                              <div className='card-body p-3'>
                                <div className='text-muted fs-6 mb-2'>{intl.formatMessage({id: 'DASHBOARD.ENTRY'})}</div>
                                <div className='fw-bold text-gray-900 fs-4'>
                                  {parseFloat(signal.entry_price).toLocaleString('fa-IR')}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {signal.stop_loss != 0 && (
                          <div className='col-4 col-md-2 col-lg-2'>
                            <div className='card bg-light-danger border-0'>
                              <div className='card-body p-3'>
                                <div className='text-danger fs-6 mb-2'>{intl.formatMessage({id: 'DASHBOARD.STOP_LOSS'})}</div>
                                <div className='fw-bold text-danger fs-4'>
                                  {parseFloat(signal.stop_loss).toLocaleString('fa-IR')}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {signal.take_profit_1 != 0 && (
                          <div className='col-4 col-md-2 col-lg-2'>
                            <div className='card bg-light-success border-0'>
                              <div className='card-body p-3'>
                                <div className='text-success fs-6 mb-2'>{intl.formatMessage({id: 'DASHBOARD.TAKE_PROFIT_1'})}</div>
                                <div className='fw-bold text-success fs-4'>
                                  {parseFloat(signal.take_profit_1).toLocaleString('fa-IR')}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {signal.take_profit_2 != 0 && (
                          <div className='col-4 col-md-2 col-lg-2'>
                            <div className='card bg-light-success border-0'>
                              <div className='card-body p-3'>
                                <div className='text-success fs-6 mb-2'>{intl.formatMessage({id: 'DASHBOARD.TAKE_PROFIT_2'})}</div>
                                <div className='fw-bold text-success fs-4'>
                                  {parseFloat(signal.take_profit_2).toLocaleString('fa-IR')}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {signal.take_profit_3 != 0 && (
                          <div className='col-4 col-md-2 col-lg-2'>
                            <div className='card bg-light-success border-0'>
                              <div className='card-body p-3'>
                                <div className='text-success fs-6 mb-2'>{intl.formatMessage({id: 'DASHBOARD.TAKE_PROFIT_3'})}</div>
                                <div className='fw-bold text-success fs-4'>
                                  {parseFloat(signal.take_profit_3).toLocaleString('fa-IR')}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {signal.take_profit_4 != 0 && (
                          <div className='col-4 col-md-2 col-lg-2'>
                            <div className='card bg-light-success border-0'>
                              <div className='card-body p-3'>
                                <div className='text-success fs-6 mb-2'>{intl.formatMessage({id: 'DASHBOARD.TAKE_PROFIT_4'})}</div>
                                <div className='fw-bold text-success fs-4'>
                                  {parseFloat(signal.take_profit_4).toLocaleString('fa-IR')}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {signal.risk_reward_ratio != 0 && (
                          <div className='col-4 col-md-2 col-lg-2'>
                            <div className='card bg-light-info border-0'>
                              <div className='card-body p-3'>
                                <div className='text-info fs-6 mb-2'>{intl.formatMessage({id: 'DASHBOARD.RISK_REWARD'})}</div>
                                <div className='fw-bold text-info fs-4'>
                                  1:{parseFloat(signal.risk_reward_ratio).toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Description & Notes */}
                      {canView && (signal.description || signal.trade_notes) && (
                        <div className='mt-4 pt-4 border-top'>
                          {signal.description && (
                            <div className='mb-3'>
                              <h5 className='fw-bold text-gray-900 mb-3 fs-4'>{intl.formatMessage({id: 'DASHBOARD.SIGNAL_DESCRIPTION'})}</h5>
                              <p className='text-gray-700 fs-6 mb-0' style={{lineHeight: '1.8'}}>
                                {signal.description}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })()
              ) : (
                <div className='d-flex align-items-center justify-content-center h-100 min-h-300px'>
                  <div className='text-center'>
                    <KTIcon iconName='chart-simple' className='fs-2x text-muted mb-3' />
                    <p className='text-muted mb-0'>{intl.formatMessage({id: 'DASHBOARD.SELECT_SIGNAL_HINT'})}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// --- KPI Card ---
interface KPICardProps {
  title: string
  value: number | string
  subtitle?: string
  icon: string
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
}

const KPICard: FC<KPICardProps> = ({title, value, subtitle, icon, tone = 'primary'}) => {
  const toneClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
      ? 'text-warning'
      : tone === 'danger'
      ? 'text-danger'
      : tone === 'info'
      ? 'text-info'
      : 'text-primary'

  return (
    <div className='card shadow-sm border-0' style={{minWidth: '180px', flex: '1 1 auto'}}>
      <div className='card-body d-flex align-items-center gap-3 p-3 p-md-4'>
        <div
          className={`rounded-circle bg-light ${toneClass}`}
          style={{
            width: '40px',
            height: '40px',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <KTIcon iconName={icon} className={`fs-3 ${toneClass.replace('text-', 'text-')}`} />
        </div>
        <div className='flex-grow-1 min-w-0'>
          <div className='fs-2hx fw-bold text-gray-900 mb-1'>{value}</div>
          <div className='fw-semibold text-gray-700 fs-7'>{title}</div>
          {subtitle && <div className='text-muted fs-8 mt-1'>{subtitle}</div>}
        </div>
      </div>
    </div>
  )
}
